import { NotificationUtils } from "@synapse-analytics/synapse-ui"
import type { MRT_ColumnDef } from "material-react-table"
import type { MomentInput } from "moment"
import moment from "moment"

import { getTheme } from "../hooks/UseTheme"

// nivo custom color scheme
// TODO:: check with design
export const customColorScheme = (): string[] => {
  const theme = getTheme()
  return [
    // 400
    theme.palette.blue.background[1],
    theme.palette.green.background[1],
    theme.palette.orange.background[1],
    theme.palette.pink.background[1],
    theme.palette.purple.background[1],
    theme.palette.red.background[1],
    theme.palette.yellow.background[1],

    // 300
    theme.palette.blue.text[2],
    theme.palette.green.text[2],
    theme.palette.orange.text[2],
    theme.palette.pink.text[2],
    theme.palette.purple.text[2],
    theme.palette.red.text[2],
    theme.palette.yellow.text[2],
  ]
}

export const customRechartsColorScheme = (): {
  background: string
  text: string
  border: string
}[] => {
  const theme = getTheme()
  return [
    {
      background: theme.palette.blue.background[1],
      text: "blue",
      border: theme.palette.blue.border[1],
    },
    {
      background: theme.palette.green.background[1],
      text: "green",
      border: theme.palette.green.border[1],
    },
    {
      background: theme.palette.yellow.background[1],
      text: "yellow",
      border: theme.palette.yellow.border[1],
    },
    {
      background: theme.palette.pink.background[1],
      text: "pink",
      border: theme.palette.pink.border[1],
    },
    {
      background: theme.palette.purple.background[1],
      text: "purple",
      border: theme.palette.purple.border[1],
    },
    {
      background: theme.palette.orange.background[1],
      text: "orange",
      border: theme.palette.orange.border[1],
    },
    {
      background: theme.palette.red.background[1],
      text: "red",
      border: theme.palette.red.border[1],
    },
  ]
}

// returns auth and backend urls based on the hostname
export function getHostnames(): {
  frontend: string
  auth: string
  backend: string
  llm: string
  registry: string
  docs: string
} {
  const hostname = window.location.hostname
  if (hostname.includes("app")) {
    const konan_hosts = {
      frontend: hostname,
      auth: hostname.replace("app", "auth"),
      backend: hostname.replace("app", "api"),
      registry: hostname.replace("app", "registry"),
      llm: hostname.replace("app", "llm"),
      docs: hostname.replace("app", "docs"),
    }
    // commenting since doxter doesn't have dev or staging envs
    // if (hostname === "app.konan.ai") {
    //   konan_hosts.doxter = "api.doxter.ai"
    // } else if (hostname === "app.staging.konan.ai") {
    //   konan_hosts.doxter = "api.dev.doxter.ai"
    // } else {
    //   konan_hosts.doxter = "api.dev.doxter.ai"
    // }
    return konan_hosts
  } else {
    return {
      frontend: hostname,
      auth: "auth.dev.konan.ai",
      backend: "api.dev.konan.ai",
      llm: "llm.dev.konan.ai",
      registry: "registry.dev.konan.ai",
      docs: "docs.dev.konan.ai",
    }
  }
}

export class KonanTimeHelper {
  adjustDate(date: moment.Moment | null, time: "start" | "end"): moment.Moment | null {
    if (date === null) return null
    /**
     * utcOffset(0) to standardize on UTC, and local() on the date before passing it to the date picker.
     * This will convert it back to the user's local time zone for display.
     */
    const adjustedDate = date.utcOffset(0).local()
    if (time === "start") {
      adjustedDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
    } else {
      adjustedDate.set({ hour: 23, minute: 59, second: 59, millisecond: 999 })
    }

    return adjustedDate
  }

  timeParser(duration: string): string | undefined {
    if (duration.includes(" ")) {
      const tempDuration = duration.split(" ")
      return `${parseInt(tempDuration[0] as string)}d`
    } else {
      const hours = duration.split(":")[0]
      if (parseInt(hours as string) > 0) {
        return `${parseInt(hours as string)}h`
      }

      const minutes = duration.split(":")[1]
      if (parseInt(minutes as string) > 0) {
        return `${parseInt(minutes as string)}m`
      }

      const seconds = duration.split(":")[2]
      if (parseInt(seconds as string) > 0) {
        return `${parseInt(seconds as string)}s`
      }
    }
  }

  formatTimeMinsAndSec = (time: number): string => {
    // Remainder of division by 60
    const seconds = time % 60
    // Divide by 60 and floor the result (get the nearest lower integer)
    const minutes = Math.abs(Math.floor(time / 60))

    // Put it all in one string
    return ("" + minutes).padStart(2, "0") + ":" + ("" + seconds).padStart(2, "0")
  }

  // check if passed date within last week or not
  lastWeekOnly = (date: moment.Moment): boolean => {
    return moment().subtract(7, "days") < date && date < moment()
  }

  // check if two dates are on the same day or not ->
  datesAreOnSameDay = (first: Date, second: Date): boolean =>
    first.getFullYear() === second.getFullYear() &&
    first.getMonth() === second.getMonth() &&
    first.getDate() === second.getDate()

  isToday(date: Date): boolean {
    const today = new Date()

    if (today.toDateString() === date.toDateString()) {
      return true
    }

    return false
  }
}

// Custom helper function to round numbers
export const customRound = (value: number, precision: number): string => {
  return (Math.round(value * 100) / 100).toFixed(value % 1 === 0 ? 0 : precision)
}

export const commify = (num: number | string): string => {
  const parts = num?.toString()?.split(".")
  const numberPart = parts[0]
  const thousands = /\B(?=(\d{3})+(?!\d))/g
  return numberPart?.replace(thousands, ",") as string
}

export const truncateString = (value: string, maxLineLength: number): string => {
  if (value && value?.length > maxLineLength) {
    return value.slice(0, maxLineLength) + "..."
  } else {
    return value
  }
}

// return tick values interval based on the date range selected
export function getTickInterval(dataLength: number): string {
  if (dataLength <= 10) {
    return "every day"
  } else if (dataLength <= 30) {
    return "every 2 days"
  } else if (dataLength <= 40) {
    return "every 4 days"
  } else if (dataLength <= 100) {
    return "every week"
  } else if (dataLength <= 150) {
    return "every 2 weeks"
  } else {
    return "every month"
  }
}

/**
 * Creates a File object from a string content.
 *
 * @param {string} content - The content of the file.
 * @param {string} filename - The name of the file.
 * @param {string} contentType - The MIME type of the file.
 * @returns {File} - The created File object.
 */
export function createFileFromString(content: string, filename: string, contentType: string): File {
  const blob = new Blob([content], { type: contentType })
  return new File([blob], filename, { type: contentType })
}

/**
 * Finds the first duplicate element in an array that has a duplicate value for a given property.
 *
 * @param arr - The array to search for duplicates.
 * @param property - The property to check for duplicates.
 * @returns The object with the duplicate property value, or undefined if no duplicates are found.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function findDuplicateElementByProperty(arr: Array<any>, property: string): any | undefined {
  const seen = new Set()

  for (const item of arr) {
    const value = item[property]

    if (seen.has(value)) {
      // Return the object with the duplicate property value
      return item
    }

    seen.add(value)
  }

  // Return undefined if no duplicates are found
  return undefined
}

/**
 * Validates if a given file has one of the allowed file extensions.
 *
 * @param {File} file - The file object to validate
 * @param {Array<string>} [extensions=[".csv"]] - Array of allowed file extensions (e.g., [".csv", ".txt"])
 * @returns {null | {code: string, message: string}} Returns null if valid, otherwise returns an error object
 *                                                   with code and message properties
 *
 * @example
 * // Validate a CSV file
 * const result = fileExtensionValidator(myFile);
 * if (result) {
 *   console.log('Invalid file:', result.message);
 * }
 *
 * @example
 * // Validate multiple extensions
 * const result = fileExtensionValidator(myFile, [".txt", ".doc", ".pdf"]);
 *
 * @throws {NotificationUtils.toast} Displays a toast notification if validation fails
 */
export function fileExtensionValidator(
  file: File,
  extensions: Array<string> = [".csv"],
): null | {
  code: string
  message: string
} {
  if (extensions.length > 0 && !extensions.some((extension) => file.name.endsWith(extension))) {
    const message =
      extensions.length === 1
        ? `Must have ${extensions[0]} extension`
        : `Must have one of the following extension: ${extensions.join(", ")}`

    NotificationUtils.toast(`Incorrect file format: ${message}`, { severity: "error" })

    return {
      code: "Wrong file format",
      message: message,
    }
  }

  return null
}

/**
 * T and K are generic function args types, inferred in function runtime
 * T is a generic type, inferred in the context of function execution
 * @param arg in this case arg is generic, T -> list item whether it's string or object
 * @param key K -> the key in the list item if item is object
 * @returns {boolean} Returns true if the object has the specified property of type string, otherwise false.
 */
function isObjectWithProperty<T, K extends keyof T>(arg: T, key?: K): arg is T & { [P in K]: string } {
  if (key === undefined) {
    return false // If key is undefined, arg cannot be an object with a string property
  }
  return typeof arg === "object" && arg !== null && key in arg && typeof arg[key] === "string"
}

/**
 * checks if string type item has an exact match from list or not
 * generic functions with generic list type, in case we want to check on list of strings
 * or list of objects that has given key in it
 * @param list list of items we iterating on, can be list of strings or objects
 * @param word the word to exact match it
 * @param key the key to compare in list, only makes sense if list items are objects not strings
 * @returns {boolean}
 */
export function hasExactMatch<T>(list: Array<T>, word: string, key?: keyof T): boolean {
  return list.some((item) => {
    if (key && isObjectWithProperty(item, key)) {
      return item[key]?.toLowerCase() === word?.toLowerCase()
    } else if (typeof item === "string") {
      return item?.toLowerCase() === word?.toLowerCase()
    } else {
      return false
    }
  })
}

/**
 * Function to extract the page from pagination backend link
 * @param {string} link
 * @returns {number}
 */
export const extractPageFromBackEndPaginationLink = (link: string): number => {
  return parseInt(link.split("page=")[1]?.split("&")[0] as string)
}

/**
 * Gets the starting position of the specified occurrence of a substring within a string.
 *
 * @param {string} string - The string to search within.
 * @param {string} subString - The substring to search for.
 * @param {number} index - The 1-based index of the occurrence to find.
 * @returns {number} The zero-based index position of the nth occurrence of the substring within the string.
 *          If the specified occurrence is not found, the length of the string is returned,
 *          which effectively points to the end of the string.
 *
 * @example
 * // returns 9
 * getPosition("/projects/e264951b-dcfa-4eab-867a-c9f4d66dcad6/models/requests", "/", 3);
 *
 * @example
 * // returns 48
 * getPosition("/projects/e264951b-dcfa-4eab-867a-c9f4d66dcad6/models/requests", "/", 5);
 */
export function getPosition(string: string, subString: string, index: number): number {
  //get the starting position of the Nth occurrence of a substring
  //for example -> getPosition(/projects/e264951b-dcfa-4eab-867a-c9f4d66dcad6/models/requests, "/", 3)
  //for example -> getPosition(/projects/e264951b-dcfa-4eab-867a-c9f4d66dcad6/models/requests, "/", 3)
  // -> get the starting position for the 3rd occurrence of "/" from the url
  return string.split(subString, index).join(subString).length
}

/**
 * Extracts the initial letters from each word in a provided string.
 *
 * @param {string} name - The full string from which to extract initial letters.
 * @returns {string} A string composed of the first letter of each word in the `name` string.
 *          If `name` is empty or if it's a single word, the first letter of the string or word is returned.
 *
 * @example
 * // returns "JD"
 * getInitialLetters("John Doe");
 *
 * @example
 * // returns "A"
 * getInitialLetters("Alice");
 */
export function getInitialLetters(name: string): string {
  return name
    .split(" ")
    .map((item) => item[0])
    .join("")
}

/**
 * Safely stringifies a value, returning the value as is if it is not an object.
 * @param value The value to stringify
 * @returns The stringified value if it is an object, otherwise the value as is
 */
// eslint-disable-next-line
export function safeStringify(value: any): string {
  // Check if the value is an object and not null (typeof null is 'object')
  if (typeof value === "object" && value !== null) {
    return JSON.stringify(value, null, 2)
  }

  // If the value is not an object, return it as is
  return value
}

/**
 * Safely parses a value, returning the original value if it is not a string.
 * @param value The value to parse
 * @returns The parsed value if it is a string, otherwise the value as is
 */
// eslint-disable-next-line
export function safeParse(value: any): any {
  // Check if the value is a string
  if (typeof value === "string") {
    try {
      // Try to parse the string as JSON
      return JSON.parse(value)
    } catch (e) {
      // If parsing fails, return the original string
      return value
    }
  }

  // If the value is not a string, return it as is
  return value
}

/**
 * Checks if the given environment variable is truthy.
 *
 * @param variable - The environment variable to check.
 * @returns A boolean indicating whether the variable is truthy or not.
 */
export function isEnvVariableTruthy(variable: string): boolean {
  return variable === "true"
}

/**
 * Annotates a number into a more readable string representation.
 * - Numbers < 1000 are returned as is
 * - Numbers >= 1000 and < 1M are formatted with 'k' suffix (e.g., 1.5k)
 * - Numbers >= 1M and < 1B are formatted with 'M' suffix (e.g., 1.5M)
 * - Numbers >= 1B and < 1T are formatted with 'B' suffix (e.g., 1.5B)
 * - Numbers >= 1T are formatted in scientific notation
 * - Decimal places are only shown if they're non-zero
 *
 * @param num - The number to format
 * @param decimals - Maximum number of decimal places to show (default: 1)
 * @returns Formatted string representation of the number
 *
 * @example
 * formatNumber(999)        // "999"
 * formatNumber(1500)       // "1.5k"
 * formatNumber(1000)       // "1k"     (no decimals when .0)
 * formatNumber(-1500)      // "-1.5k"
 * formatNumber(1500000)    // "1.5M"
 * formatNumber(0.123, 2)   // "0.12"
 */
export function annotateNumber(num: number, decimals = 1): string {
  // Get absolute value for comparisons and sign for later use
  const absNum = Math.abs(num)
  const sign = num < 0 ? "-" : ""

  /**
   * Helper function to format a number with decimals and remove trailing zeros
   * @param value - The number to format
   * @returns Formatted string without unnecessary decimals
   */
  const formatDecimals = (value: number): string => {
    const formatted = value.toFixed(decimals)
    // Remove trailing zeros after decimal point
    return formatted.replace(/\.?0+$/, "")
  }

  // Handle very small numbers (between 0 and 1)
  if (absNum < 1 && absNum > 0) {
    return sign + formatDecimals(absNum)
  }

  // Handle numbers less than 1000
  if (absNum < 1000) {
    return sign + (Number.isInteger(absNum) ? absNum.toString() : formatDecimals(absNum))
  }

  // Handle thousands (k)
  if (absNum < 1000000) {
    return sign + formatDecimals(absNum / 1000) + "k"
  }

  // Handle millions (M)
  if (absNum < 1000000000) {
    return sign + formatDecimals(absNum / 1000000) + "M"
  }

  // Handle billions (B)
  if (absNum < 1000000000000) {
    return sign + formatDecimals(absNum / 1000000000) + "B"
  }

  // For numbers larger than 1 trillion, use scientific notation
  // Remove trailing zeros for scientific notation too
  return num.toExponential(decimals).replace(/\.?0+e/, "e")
}

/**
 * Checks if a string does NOT contain any of the specified substrings
 *
 * @param {string} input The string to check
 * @param {string[]} substrings string to check against
 * @returns {boolean} True if the string doesn't contain any of the given substrings
 */
export const doesNotContainSubstrings = (input: string | undefined, substrings: string[]): boolean => {
  // First, check if the string is not empty or just whitespace
  if (!input || input.trim().length === 0) {
    return false
  }

  // Filter out undefined values and create a new array with only valid strings
  const validSubstrings = substrings.filter(
    (item): item is string => typeof item === "string" && item.trim().length > 0,
  )

  // If there are no valid substrings to check against, return false
  if (validSubstrings.length === 0) {
    return false
  }

  // loop on all valid substrings and check if the input includes them
  for (const substring of validSubstrings) {
    if (input.includes(substring)) return false
  }

  return true
}

/**
 * Checks if date given is the same as today's date
 *
 * @param {MomentInput} date The date to check against
 * @returns {boolean} True if the date given matches today's date
 */
export const isToday = (date: MomentInput): boolean => {
  return moment(date).isSame(moment(), "day")
}

/**
 * Calculates and returns the time difference between now and a given time as a human-readable string like "2 days ago" or "in a month".
 * Link to docs: https://momentjs.com/docs/#/durations/humanize/
 *
 * @param {MomentInput} date The date to check against
 * @returns {string} The time difference as a human-readable string
 */
export const formatDistance = (date: MomentInput): string => {
  // Uses a 31-day month threshold to configure when moment starts to show "in a month" string
  // The '-' sign is used so change the way moment.js formats the string.
  return moment.duration(-moment().diff(date)).humanize(true, { d: 31 })
}

/**
 * Converts a CSV string into an array of objects where each object represents a row
 * with keys from the header row and corresponding values.
 *
 * @param csvString - The input CSV string with headers in the first row
 * @returns An array of objects where each object contains key-value pairs from the CSV
 *
 * @example
 * const csv = "name,age\nJohn,30\nJane,25";
 * const result = csvToJson(csv);
 * // Returns: [
 * //   { name: "John", age: "30" },
 * //   { name: "Jane", age: "25" }
 * // ]
 */
export function convertCsvToJson(csvString: string): Array<Record<string, string | null>> {
  // Split the CSV string into rows and filter out empty rows
  const rows = csvString.split("\n").filter((row) => row.trim() !== "")

  // If no rows after filtering, return empty array
  if (rows.length === 0) return []

  // Extract headers from the first row
  const headers = rows[0]?.split(",")

  const json: Array<Record<string, string | null>> = []
  // Start from index 1 to skip the header row
  for (let i = 1; i < rows.length; i++) {
    const values = rows[i]?.split(",")

    // Skip if row has no values
    if (!values?.length) continue

    const obj: Record<string, string | null> = {}

    if (!headers) continue
    // Map each value to its corresponding header
    for (let j = 0; j < headers.length; j++) {
      const key = headers[j]?.trim()
      const value = values[j]?.trim() || null // Handle undefined values

      if (!key) continue // Skip if key is undefined

      obj[key] = value
    }

    json.push(obj)
  }
  return json
}

/**
 * Read file content as text
 * @param file File to read
 * @param limit Number of rows/items to return (-1 for all)
 * @returns Promise that resolves with the file content as a string
 * @throws Error if the file content is not a string
 */
export const readFileContentAsync = (file: File, limit: number = -1): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (event) => {
      const result = event?.target?.result
      if (typeof result === "string") {
        if (file.name.endsWith(".json")) {
          let jsonData = []

          try {
            jsonData = JSON.parse(result)
          } catch {
            return reject(new Error("Invalid File"))
          }

          if (!Array.isArray(jsonData)) return reject("JSON file must be in array format")
          const limitedData = limit > 0 ? jsonData.slice(0, limit) : jsonData
          return resolve(JSON.stringify(limitedData, null, "\t"))
        } else if (file.name.endsWith(".csv")) {
          if (limit <= 0) {
            return resolve(result)
          } else {
            const lines = result.split("\n")
            const header = lines[0]
            const dataLines = lines.slice(1, limit + 1)
            return resolve([header, ...dataLines].join("\n"))
          }
        } else {
          return resolve(result)
        }
      } else {
        return reject(new Error("File content is not a string"))
      }
    }

    reader.onerror = (error) => reject(error)

    reader.readAsText(file)
  })
}

/**
 * Recursively gets all possible key paths in an object, using dot notation for nested properties.
 *
 * @param {Record<PropertyKey, any>} obj - The object to traverse
 * @param {string} [parent=""] - The parent path (used internally for recursion)
 * @returns {Array<string>} An array of strings representing all possible key paths in the object
 *
 * @example
 * const obj = {
 *   name: "John",
 *   age: 30,
 *   address: {
 *     street: "123 Main St",
 *     city: {
 *       name: "Boston",
 *       zip: "02108"
 *     }
 *   }
 * };
 *
 * getKeysPathsRecursively(obj);
 * // Returns:
 * // [
 * //   "name",
 * //   "age",
 * //   "address.street",
 * //   "address.city.name",
 * //   "address.city.zip"
 * // ]
 */
export function getKeysPathsRecursively<T extends {}>(obj: T, parent: string = ""): Array<string> {
  const keys: Array<string> = []

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const fullPath = parent ? `${parent}.${key}` : key

      // If the value is an object, recurse into it
      if (typeof obj[key] === "object" && obj[key] !== null && !Array.isArray(obj[key])) {
        keys.push(...getKeysPathsRecursively(obj[key], fullPath))
      } else {
        keys.push(fullPath)
      }
    }
  }

  return keys
}

export const isInputValueValid = (
  type: "label" | "workflow" | "rule",
  value: string,
  prohibitedKeywords: string[],
): boolean => {
  if (prohibitedKeywords.some((keyword) => keyword === value.trim())) {
    NotificationUtils.toast(`The ${type} name field can not be ${prohibitedKeywords.join(" or ")}`, {
      severity: "error",
    })

    return false
  }
  return true
}

/**
 * Type definition for a recursively nested record where:
 * - keys are strings
 * - values are either numbers or more nested records
 */
export type NestedRecord = { [key: string]: number | NestedRecord }

/**
 * Extracts all unique keys found at the deepest levels of a nested record structure.
 *
 * A "deepest level" is defined as any object where the values are no longer objects themselves.
 * This function will return a deduplicated array of all such keys.
 *
 * @param data - A nested object with string keys and values that are either numbers or nested objects.
 * @returns An array of unique string keys found at the deepest level(s) of nesting.
 */
export function extractDeepestKeys(data: NestedRecord): string[] {
  const uniqueKeys = new Set<string>()

  /**
   * Recursively traverses the nested record to collect keys at the deepest level.
   *
   * @param record - The current object node being inspected
   */
  function recurse(record: NestedRecord): void {
    for (const key in record) {
      const value = record[key]
      if (typeof value === "object" && value !== null) {
        // If the value is an object, recurse deeper
        recurse(value)
      } else {
        // If the value is a primitive (number), record the key
        uniqueKeys.add(key)
      }
    }
  }

  recurse(data)
  return Array.from(uniqueKeys)
}

/**
 * Extracts the headers from a CSV file.
 *
 * This function reads the contents of a provided `File` object,
 * assumes the first line contains comma-separated headers,
 * and returns them as a trimmed string array.
 *
 * @param file - A CSV file (typically from an <input type="file"> element).
 * @returns A Promise that resolves to an array of header strings,
 *          or rejects if the file cannot be read.
 *
 * @example
 * ```ts
 * const fileInput = document.querySelector('input[type="file"]');
 * fileInput?.addEventListener('change', async (event) => {
 *   const file = (event.target as HTMLInputElement).files?.[0];
 *   if (file) {
 *     const headers = await extractCSVHeadersFromFile(file);
 *     console.log(headers); // ['name', 'age', 'email']
 *   }
 * });
 * ```
 */
export function extractCSVHeadersFromFile(file: File): Promise<string[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (event: ProgressEvent<FileReader>) => {
      const text = event.target?.result
      if (typeof text !== "string") {
        reject(new Error("Failed to read file content as text."))
        return
      }

      const lines = text.trim().split("\n")
      if (lines.length === 0) {
        resolve([])
        return
      }

      const headers = lines[0]?.split(",").map((header) => header.trim()) ?? []
      resolve(headers)
    }

    reader.onerror = (error) => {
      reject(error)
    }

    reader.readAsText(file)
  })
}

/**
 * Validates if a file has one of the allowed extensions.
 *
 * @param file - The file to validate.
 * @param allowedExtensions - An array of allowed extensions (e.g. ['.png', '.jpg', '.jpeg']).
 * @returns Null if valid, otherwise an error object.
 */
export const validateFileFormat = (
  file: File,
  allowedExtensions: string[],
): null | {
  code: string
  message: string
} => {
  const fileExtension = file.name.slice(file.name.lastIndexOf(".")).toLowerCase()

  if (!allowedExtensions.includes(fileExtension)) {
    NotificationUtils.toast(`File must be in one of these formats: ${allowedExtensions.join(", ")}`, {
      title: "Incorrect file format",
      severity: "error",
    })

    return {
      code: "Incorrect file format",
      message: `File must be one of: ${allowedExtensions.join(", ")}`,
    }
  }

  return null
}

/**
 * Validates if a file is below the specified max file size.
 *
 * @param file - The file to validate.
 * @param maxFileSizeInMB - Maximum file size in MB.
 * @returns Null if valid, otherwise an error object.
 */
export const validateFileSize = (
  file: File,
  maxFileSizeInMB: number,
): null | {
  code: string
  message: string
} => {
  const maxFileSize = maxFileSizeInMB * 1024 * 1024

  if (file.size > maxFileSize) {
    NotificationUtils.toast(`File size has to be smaller than ${maxFileSizeInMB}MB`, {
      title: "File is too big",
      severity: "error",
    })

    return {
      code: "File is too big",
      message: `File size has to be smaller than ${maxFileSizeInMB}MB`,
    }
  }

  return null
}

/**
 * Retrieves the value of an environment variable.
 *
 * @param envVar - The name of the environment variable to retrieve.
 * @param defaultValue - An optional default value to return if the environment variable is not set.
 * @returns The environment variable value if defined, otherwise the default value, or an empty string if neither is provided.
 */
export const getEnvVar = (envVar: string, defaultValue?: string | number): string | number | undefined => {
  return window.__RUNTIME_CONFIG__[envVar as keyof typeof window.__RUNTIME_CONFIG__] ?? defaultValue ?? undefined
}

/**
 * Formats a file size in bytes into a human-readable string in KB or MB.
 *
 * This function checks if the size is 1 MB or larger. If it is, the size is
 * converted to megabytes (MB). Otherwise, it's converted to kilobytes (KB).
 * The final value is always rounded to two decimal places.
 *
 * @param fileSize - The size of the file in bytes.
 * @returns A formatted string representing the file size with the appropriate unit (e.g., "1.50 MB" or "55.00 KB").
 *
 * @example
 * ```typescript
 * const largeFile = formatFileSize(1572864); // Returns "1.50 MB"
 * const smallFile = formatFileSize(56320);   // Returns "55.00 KB"
 * ```
 */
export const formatFileSize = (fileSize: number): string => {
  if (fileSize >= 1024 * 1024) {
    return (fileSize / (1024 * 1024)).toFixed(2) + " MB"
  } else {
    return (fileSize / 1024).toFixed(2) + " KB"
  }
}

/**
 * Dynamically generates table column definitions from an array of data objects.
 * It iterates through the data to find all unique keys and creates a column for each.
 * @param data - The array of objects to derive columns from.
 * @returns An array of MRT_ColumnDef objects.
 */
export const generateTableColumnsFromData = (data: Array<Record<string, unknown>>): MRT_ColumnDef[] => {
  // Return an empty array if there's no data to process
  if (!data || data.length === 0) {
    return []
  }

  // Use a Set to automatically handle unique keys
  const keys = new Set<string>()
  for (const row of data) {
    for (const key in row) {
      if (key) {
        // Ensure the key is not an empty string
        keys.add(key)
      }
    }
  }

  // Convert the set of unique keys into an array of column definitions
  return Array.from(keys).map((key) => ({
    header: key,
    accessorKey: key,
    enableColumnFilter: false,
  }))
}
